import { View } from 'react-native'
import { MODAL_BOTTOM_SCREEN_GAP } from '@ca/ui/components/Modal/variants/ModalBottom'
import { MESSAGES_CONTAINER_PADDING_VERTICAL } from '@ca/ui/features/measurement/Messages/styles'
import Typography from '@ca/ui/typography'
import type { SHCaptureAggregateData } from '../../src/ExpoOpticalMeasure.types'

/**
 * Dev-only readout of what varied *within* the capture being reviewed (plan
 * A4), opposite the close chip.
 *
 * It exists to answer one question a bench repeat set cannot answer without it:
 * when the reported value moves several millimetres between captures, is that
 * per-frame noise — which more samples would average away — or a per-capture
 * systematic, which they would not? Compare the dispersion here against the
 * spread of the reported values across the set. If the latter is much larger,
 * Phase A is not the lever and the scale/pose/distance columns say where to
 * look instead.
 */
const formatMm = (value: number | undefined, digits = 2) =>
  value == null ? '—' : value.toFixed(digits)

export function ShCaptureStatsLine({
  aggregate,
}: {
  aggregate: SHCaptureAggregateData | undefined
}) {
  if (!aggregate) return null

  const isMad = (aggregate.spreadMetric ?? 'range') === 'mad'
  const [left, right] = isMad
    ? [aggregate.dispersionLeft, aggregate.dispersionRight]
    : [aggregate.spreadLeft, aggregate.spreadRight]

  const parts = [
    `n${aggregate.sampleCount}`,
    `${isMad ? 'σ' : 'rng'} ${formatMm(left)}/${formatMm(right)}`,
    aggregate.scaleCv == null
      ? null
      : `scaleCv ${(aggregate.scaleCv * 100).toFixed(2)}%`,
    aggregate.distanceMeanCm == null
      ? null
      : `d ${formatMm(aggregate.distanceMeanCm, 1)}±${formatMm(
          aggregate.distanceSpreadCm,
          1
        )}cm`,
    aggregate.pitchMeanDeg == null
      ? null
      : `pitch ${formatMm(aggregate.pitchMeanDeg, 1)}±${formatMm(
          aggregate.pitchSpreadDeg,
          1
        )}°`,
    aggregate.gazeMeanDeg == null
      ? null
      : `gaze ${formatMm(aggregate.gazeMeanDeg, 1)}±${formatMm(
          aggregate.gazeSpreadDeg,
          1
        )}°`,
    aggregate.scaleSource == null
      ? null
      : `${aggregate.scaleSource}${aggregate.scaleSourceSwitched ? '!' : ''}`,
  ].filter((part) => part != null)

  return (
    <View
      className="absolute"
      pointerEvents="none"
      style={{
        left: MODAL_BOTTOM_SCREEN_GAP,
        top: MESSAGES_CONTAINER_PADDING_VERTICAL,
      }}
    >
      <Typography variant="XS" className="text-white/70">
        {parts.join(' · ')}
      </Typography>
    </View>
  )
}
